// The insurance backend, typed. Everything an account has in common with the
// exchange — sign-in, the PIN, profile, registration — comes from
// @argo/common/api and is re-exported, so a screen imports one module.
import { API_BASE, authToken, del, get, post, postForm, put, api as common } from '@argo/common/api'

export * from '@argo/common/api'

/** Money is BIGINT on the wire: exact as a string, never a lossy double. */
export type Money = string

export type Product = {
  code: string; name: string; description: string
  product_type: string; service_code: string; provider_code: string
  rate_bps: number | null
  insurer: string | null; coverage_limit: Money | null; term_days: number | null
  refund: 'none' | 'pro_rata' | null; active: boolean
  /** A bond holds the buyer's own money in escrow for a landlord; cover is insurance. */
  kind: 'cover' | 'bond'; escrow: string | null; release_days: number
  max_commission_bps: number; referral_bps: number
}

export type Policy = {
  id: string; product: string; product_name: string | null
  holder: string; insurer: string
  coverage: Money; premium: Money; remaining: Money; claimed: Money
  starts_at: string; ends_at: string
  refund: 'none' | 'pro_rata'
  status: 'active' | 'expired' | 'cancelled' | 'released'
  cancelled_at: string | null; refunded: Money
  kind: 'cover' | 'bond'; beneficiary: string; referrer: string | null; commission: Money
  pricing_ref: string | null; open_claims: number
  escrow: string | null; release_at: string | null; released: Money
  external?: ExternalPolicy | null
}

/** Where a policy of ours is also a contract in the industry's system. */
export type ExternalPolicy = {
  source: string; status: 'registered' | 'pending' | 'cancelled'
  external_id: string; number: string | null; pdf_url: string | null
  premium: Money; starts_at: string | null; ends_at: string | null; note: string
}

export type Claim = {
  id: string; policy: string; product_name: string | null
  holder: string; insurer: string
  amount: Money; paid: Money; outstanding: Money
  incurred_at: string; reported_at: string; last_paid_at: string | null; closed_at: string | null
  evidence_sha256: string
  status: 'open' | 'settled' | 'rejected'
  /** Who pays it: the insurer, or the escrow under a bond. */
  payer: string; policyholder: string; kind: 'cover' | 'bond'
}

export type Payment = { txid: string; amount: Money; paid_at: string }
export type Decision = { action: 'pay' | 'settle' | 'reject'; amount: Money | null; reason: string; decided_by: string; decided_at: string }
export type Evidence = { filename: string; content_type: string; size_bytes: Money }

export type Balances = { STABLE: Money; PREMIUM: Money; CLAIM: Money; BOND: Money }

/** A balance held outside this ledger, shown beside it — the brand's ABC score. */
export type External = { source: string; label: string; amount: Money; test: boolean }

export type Vehicle = {
  id: string; plate: string; vehicle_class: string; make_model: string
  year: number | null; engine_cc: number | null; value: Money | null
}

/** What pricing reads about the holder: age and sex from the registration number, the rest theirs to edit. */
export type Insured = {
  birth_date: string | null; age?: number; sex?: 'M' | 'F'
  licence_year: number | null; vehicles: Vehicle[]
}

export type QuoteLine = { name: string; fact: string; value: string; factor: string }

/** A price — or, when the tariff lacks a fact, which ones it needs. */
/**
 * A compulsory contract is written in the industry's own system, so the
 * premium, the number and the vehicle come back from there rather than from
 * our tariff. The holder is shown them before paying.
 */
export type ExternalQuote = {
  source: string; policy_number: string; insuree_name: string
  starts_at: string; ends_at: string
  /** The registry's own record, in its own field names. */
  vehicle?: { plateNumber?: string; markName?: string; modelName?: string; buildYear?: number; className?: string }
  drivers?: { name?: string; registerNumber?: string; register_number?: string }[]
}

export type Quote = {
  product: string; uses: string[]; labels: Record<string, string>; options: Record<string, string[]>
  complete: boolean; needs?: string[]; facts: Record<string, string | number>
  /** The cover is the law's, not the buyer's: the form shows it, it does not ask. */
  fixed_cover?: boolean; external?: ExternalQuote
  quote?: string; coverage?: number; term_days?: number; premium?: number; base?: string
  breakdown?: QuoteLine[]; pricing_ref?: string; expires_at?: string; bond?: number
  referral?: { code: string; name: string }; beneficiary?: { account: string; name: string }
}

export type PolicyEvidence = {
  sha256: string; note: string; uploaded_by: string; uploaded_at: string
  filename: string; content_type: string; size_bytes: Money
}

export type ReferralMine = {
  codes: { code: string; label: string; created_at: string; disabled_at: string | null }[]
  policies: { id: string; product: string; product_name: string | null; premium: Money; commission: Money; starts_at: string; status: string }[]
  earned: Money
}

export type ReferralCode = {
  code: string; account: string; label: string; created_by: string; created_at: string
  disabled_at: string | null; rates: { product: string; rate_bps: number }[]
}

export type PricingVersion = {
  id: string; method: 'flat_rate' | 'factor_table'; params: unknown; note: string
  created_by: string; created_at: string; retired_at: string | null
}

/** Who the caller is here: their account, and which of the insurer's desks they work. */
export type InsuranceMe = {
  subject: string; username: string; account: string; roles: string[]
  staff: { underwriter: boolean; claims: boolean; actuary: boolean; issuer: boolean }
  pin: { set: boolean; locked_until: string }
  balances: Balances
}

export type Report = {
  as_of: string; insurer: string; policies: number; claims: number
  statement: {
    written_premium: number; unearned_premium: number; earned_premium: number
    paid_claims: number; outstanding_claims: number; ibnr: number; total_claims: number
    bad_debt: number; operating_expenses: number; underwriting_margin: number
  }
  balance_sheet: Record<string, number>
  triangle: {
    developments: number[]
    factors: number[]
    rows: { age: number; paid: number[]; cumulative: number[]; reported: number; ultimate: number }[]
  }
  ledger_stable?: Money
}

const qs = (o: Record<string, string | number | boolean | undefined>) => {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== '' && v !== false) p.set(k, String(v))
  const s = p.toString()
  return s ? `?${s}` : ''
}

export const api = {
  ...common,
  me: () => get<InsuranceMe>('/me'),
  balances: (account?: string) => get<{ account: string; data: Balances }>(`/balances${qs({ account })}`),

  products: () => get<{ data: Product[] }>('/products'),
  quote: (code: string, coverage: number) =>
    get<{ product: string; coverage: number; premium: number; term_days: number }>(
      `/products/${encodeURIComponent(code)}/quote${qs({ coverage })}`),
  defineProduct: (body: Record<string, unknown>) => post<{ tx_id: string }>('/products', body),

  policies: (opts: { all?: boolean; status?: string } = {}) => get<{ data: Policy[] }>(`/policies${qs(opts)}`),
  policy: (id: string) => get<{ data: Policy; claims: Claim[]; evidence: PolicyEvidence[] }>(`/policies/${encodeURIComponent(id)}`),
  buy: (body: { product: string; coverage: number; pin?: string }) => post<{ policy: Policy; tx_id: string }>('/policies', body),
  /** Buys a quote: `confirm` is the holder saying the facts it was priced from are true. */
  buyQuote: (body: { quote: string; confirm: true; pin?: string }) => post<{ policy: Policy; tx_id: string }>('/policies', body),
  attach: (id: string, form: FormData) => postForm<{ data: Policy; evidence: PolicyEvidence[] }>(`/policies/${encodeURIComponent(id)}/evidence`, form),
  endLease: (id: string) => post<{ policy: Policy; tx_id: string }>(`/policies/${encodeURIComponent(id)}/end`, {}),

  external: () => get<{ data: External[] }>('/balances/external'),
  insured: () => get<Insured>('/insured'),
  setLicenceYear: (year: number | null) => put<Insured>('/insured', { licence_year: year }),
  addVehicle: (v: Partial<Vehicle>) => post<Insured & { added: string }>('/insured/vehicles', v),
  changeVehicle: (id: string, v: Partial<Vehicle>) => put<Insured>(`/insured/vehicles/${encodeURIComponent(id)}`, v),
  removeVehicle: (id: string) => del<Insured>(`/insured/vehicles/${encodeURIComponent(id)}`),
  quoteFor: (body: { product: string; coverage?: number; vehicle?: string; choices?: Record<string, string>;
                     referral?: string; beneficiary?: string }) => post<Quote>('/quotes', body),

  referralCheck: (code: string, product: string) =>
    get<{ code: string; name: string }>(`/referrals/check${qs({ code, product })}`),
  referralsMine: () => get<ReferralMine>('/referrals/mine'),
  referralCodes: () => get<{ data: ReferralCode[] }>('/referrals'),
  createReferral: (body: { code: string; account: string; label?: string }) => post<{ code: string }>('/referrals', body),
  setReferralRate: (code: string, body: { product: string; rate_bps: number }) =>
    put<{ rate_bps: number }>(`/referrals/${encodeURIComponent(code)}/rates`, body),
  disableReferral: (code: string) => post<object>(`/referrals/${encodeURIComponent(code)}/disable`, {}),

  pricingVersions: (code: string) => get<{ data: PricingVersion[] }>(`/products/${encodeURIComponent(code)}/pricing`),
  setPricing: (code: string, body: { method: string; params: unknown; note?: string }) =>
    post<{ version: number; uses: string[] }>(`/products/${encodeURIComponent(code)}/pricing`, body),
  cancel: (id: string, pin?: string) => post<{ policy: Policy; tx_id: string }>(`/policies/${encodeURIComponent(id)}/cancel`, { pin }),

  claims: (opts: { all?: boolean; status?: string } = {}) => get<{ data: Claim[] }>(`/claims${qs(opts)}`),
  claim: (id: string) => get<{ data: Claim; payments: Payment[]; decisions: Decision[]; evidence: Evidence[] }>(
    `/claims/${encodeURIComponent(id)}`),
  file: (form: FormData) => postForm<{ claim: Claim; tx_id: string }>('/claims', form),
  pay: (id: string, body: { amount: number; pin?: string; reason?: string }) =>
    post<{ claim: Claim; tx_id: string }>(`/claims/${encodeURIComponent(id)}/pay`, body),
  settle: (id: string, reason: string) => post<{ claim: Claim; tx_id: string }>(`/claims/${encodeURIComponent(id)}/settle`, { reason }),
  reject: (id: string, reason: string) => post<{ claim: Claim; tx_id: string }>(`/claims/${encodeURIComponent(id)}/reject`, { reason }),

  mint: (body: { account: string; amount: number; pin?: string }) => post<{ tx_id: string }>('/stable/mint', body),

  report: (opts: { as_of?: string; insurer?: string; capital?: string }) => get<Report>(`/reports/underwriting${qs(opts)}`),
}

/**
 * Downloads a file the API serves to signed-in callers only. A plain link
 * would arrive without the Authorization header.
 */
export async function download(path: string, filename: string): Promise<void> {
  const res = await fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${authToken() ?? ''}` } })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * The policies a caller may claim on: their own cover with cover left, or a
 * bond that names them as its landlord and has not been released.
 */
export function claimable(policies: Policy[], account: string): Policy[] {
  return policies.filter(p => Number(p.remaining) > 0 && (
    p.kind === 'bond'
      ? p.beneficiary === account && p.status !== 'released'
      : p.holder === account && p.status !== 'cancelled'))
}

/** Facts that come from the vehicle chosen, rather than from a question. */
export const VEHICLE_FACTS = ['vehicle_class', 'vehicle_age', 'engine_cc', 'vehicle_value']
/** Facts read from the registration number or the profile, never chosen on a form. */
export const PROFILE_FACTS = ['age', 'sex', 'driving_years']

/** A date as the input element wants it, in the insurer's time zone. */
export function today(): string {
  return new Date().toLocaleDateString('en-CA')
}

/**
 * Money, as a Mongolian reader expects it: whole tögrög, grouped, with the
 * sign attached — 71,280₮. This ledger has no fractions, and a figure that
 * carries them (a reserve, a projection) is rounded to the nearest tögrög
 * rather than shown to the kopek, which nobody here spends.
 */
export function money(v: Money | number | null | undefined): string {
  const n = Number(v ?? 0)
  return Number.isFinite(n) ? `${Math.round(n).toLocaleString()}₮` : '-'
}

/** A number that is not money — a score, a count — so it gets no ₮. */
export function plain(v: number | string | null | undefined): string {
  const n = Number(v ?? 0)
  return Number.isFinite(n) ? n.toLocaleString() : '-'
}

/** A report figure, which does have decimals. */
export function decimal(v: number | undefined, digits = 2): string {
  return Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

export function day(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString()
}

/** How much of the cover is left, 0..1, for the meter on a policy card. */
export function coverLeft(p: Policy): number {
  const total = Number(p.coverage), left = Number(p.remaining)
  if (!Number.isFinite(total) || total <= 0) return 0
  return Math.max(0, Math.min(1, left / total))
}
